/**
 * Firebase runtime bootstrap — SINGLE source of the active Firebase app for the
 * whole Tranzit web app.
 *
 * Why this exists (Phase 4 — client failover):
 *   The app initializes Firebase in a few places (config/firebase.ts,
 *   firebaseWithFailover.ts, firebase.ts). For a clean DR cutover we must pick
 *   ONE active project (primary | secondary) at app bootstrap and have every
 *   module initialize with the SAME config — Firebase throws `app/duplicate-app`
 *   if the default app is initialized twice with *different* options.
 *
 * How the active project is chosen (in priority order):
 *   1. `window.__TRANZIT_RUNTIME_CONFIG__.activeFirebaseProject` — injected by the
 *      static, non-bundled `/runtime-config.js` (editable post-deploy → cutover
 *      = edit file + reload, no rebuild). Default file ships "primary".
 *   2. `REACT_APP_ACTIVE_FIREBASE_PROJECT` build-time override (escape hatch).
 *   3. Fallback: "primary".
 *
 * Safety / dormancy:
 *   - "secondary" is honored ONLY when a complete secondary config is present
 *     (all `REACT_APP_FIREBASE_*_SECONDARY` set). Otherwise we stay on primary
 *     and log a warning. With no secondary env (today), behavior is byte-for-byte
 *     identical to before this module existed.
 *   - App initialization is guarded with `getApps()` so import order is irrelevant.
 */

import {
  initializeApp,
  getApps,
  getStorage,
  getAuth,
  getFirestore,
  getFunctions,
} from '../services/firebaseImports';
import { initFirebaseAppCheck } from '../utils/firebaseAppCheck';
import Logger from '../services/logger';
import {
  FAILOVER_PROJECT_PRIMARY,
  FAILOVER_PROJECT_SECONDARY,
  type FailoverActiveProject,
} from '../constants/failoverControl';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

const primaryEnv = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const missingPrimary = Object.entries(primaryEnv)
  .filter(([, v]) => !v)
  .map(([k]) => k);

if (missingPrimary.length > 0) {
  throw new Error(
    `Missing required Firebase environment variables: ${missingPrimary.join(', ')}. ` +
      `Please set these in your .env file or environment configuration.`
  );
}

export const primaryConfig: FirebaseConfig = {
  apiKey: primaryEnv.apiKey!,
  authDomain: primaryEnv.authDomain!,
  projectId: primaryEnv.projectId!,
  storageBucket: primaryEnv.storageBucket!,
  messagingSenderId: primaryEnv.messagingSenderId!,
  appId: primaryEnv.appId!,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || undefined,
};

const secondaryEnv = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY_SECONDARY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_SECONDARY,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_SECONDARY,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_SECONDARY,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_SECONDARY,
  appId: process.env.REACT_APP_FIREBASE_APP_ID_SECONDARY,
};

export const secondaryConfigIsComplete = Object.values(secondaryEnv).every(
  (v) => typeof v === 'string' && v.trim() !== ''
);

export const secondaryConfig: FirebaseConfig | null = secondaryConfigIsComplete
  ? {
      apiKey: secondaryEnv.apiKey!,
      authDomain: secondaryEnv.authDomain!,
      projectId: secondaryEnv.projectId!,
      storageBucket: secondaryEnv.storageBucket!,
      messagingSenderId: secondaryEnv.messagingSenderId!,
      appId: secondaryEnv.appId!,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_SECONDARY || undefined,
    }
  : null;

function readRuntimeSelector(): FailoverActiveProject {
  // 1) Static runtime-config injected by /runtime-config.js (no rebuild to flip).
  try {
    if (typeof window !== 'undefined') {
      const rc = (window as unknown as {
        __TRANZIT_RUNTIME_CONFIG__?: { activeFirebaseProject?: unknown };
      }).__TRANZIT_RUNTIME_CONFIG__;
      if (rc && rc.activeFirebaseProject === FAILOVER_PROJECT_SECONDARY) {
        return FAILOVER_PROJECT_SECONDARY;
      }
    }
  } catch {
    /* ignore — fall through to defaults */
  }
  // 2) Build-time override (escape hatch for environments without runtime-config.js).
  if (process.env.REACT_APP_ACTIVE_FIREBASE_PROJECT === FAILOVER_PROJECT_SECONDARY) {
    return FAILOVER_PROJECT_SECONDARY;
  }
  // 3) Default.
  return FAILOVER_PROJECT_PRIMARY;
}

const requested = readRuntimeSelector();

/** The project this bundle actually booted against. */
export const ACTIVE_FIREBASE_PROJECT: FailoverActiveProject =
  requested === FAILOVER_PROJECT_SECONDARY && secondaryConfig
    ? FAILOVER_PROJECT_SECONDARY
    : FAILOVER_PROJECT_PRIMARY;

if (requested === FAILOVER_PROJECT_SECONDARY && !secondaryConfig) {
  Logger.warn(
    '[firebaseRuntime] Runtime config requested the secondary Firebase project, but the ' +
      'secondary configuration is incomplete (REACT_APP_FIREBASE_*_SECONDARY). Staying on primary.'
  );
}

const activeConfig: FirebaseConfig =
  ACTIVE_FIREBASE_PROJECT === FAILOVER_PROJECT_SECONDARY && secondaryConfig
    ? secondaryConfig
    : primaryConfig;

if (ACTIVE_FIREBASE_PROJECT === FAILOVER_PROJECT_SECONDARY) {
  Logger.info(
    `[firebaseRuntime] Booting against SECONDARY Firebase project (${activeConfig.projectId}).`
  );
}

// Guarded init — first importer creates the default app; everyone else reuses it.
// (Firebase throws app/duplicate-app only when re-initialized with *different* options,
//  which cannot happen here because all default-app init now flows through this module.)
const existingApps = getApps();
const app = existingApps.length ? existingApps[0]! : initializeApp(activeConfig);

initFirebaseAppCheck(app);

const storage = getStorage(app);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');

export { app as default, storage, auth, db, functions };
